import React from 'react';
import { Chart as ChartJS } from 'chart.js';
import { useLocation } from 'react-router-dom';

import { parseHiddenChartItems } from './hiddenChartItems';

export type ChartItemsVisibilityProps = {
  itemsVisibility: boolean[];
  onItemClicked: (datasetIndex: number) => void;
};

export type ChartConfig = {
  id: string;
  type: 'data' | 'dataset';
};

function useChartItemsVisibility(charts: ChartConfig[], itemsSize = 0) {
  const { search } = useLocation();
  // Frozen at mount: hidden items handed over via the URL (e.g. by the export tab opener).
  // All charts of one hook share a single legend and are index-synced by toggleVisibility,
  // so the first chart id found in the URL stands for the whole group.
  const [initialHiddenItems] = React.useState<number[]>(() => {
    const hiddenItemsByChartId = parseHiddenChartItems(search);
    const hiddenItems = charts.map((chart) => hiddenItemsByChartId[chart.id]).find((items) => items?.length);
    return hiddenItems ?? [];
  });

  const buildInitialVisibility = React.useCallback(
    (size: number) => Array.from({ length: size }, (_, index) => !initialHiddenItems.includes(index)),
    [initialHiddenItems]
  );

  const [itemsVisibility, setitemsVisibility] = React.useState<boolean[]>(buildInitialVisibility(itemsSize));

  const toggleVisibility = React.useCallback(
    (index: number) => {
      charts.forEach((chart) => {
        const chartInstance = ChartJS.getChart(chart.id);
        if (!chartInstance) return;

        if (chart.type === 'data') {
          chartInstance.toggleDataVisibility(index);
        } else {
          // dataset
          const isVisible = chartInstance.isDatasetVisible(index);
          chartInstance.setDatasetVisibility(index, !isVisible);
        }
        chartInstance.update();
      });

      setitemsVisibility((prev) => {
        const newItemsVisibility = [...prev];
        newItemsVisibility[index] = !newItemsVisibility[index];
        return newItemsVisibility;
      });
    },
    [charts]
  );

  const restoreVisibility = React.useCallback(
    (size: number) => {
      const newItemsVisibility = buildInitialVisibility(size);
      setitemsVisibility(newItemsVisibility);

      charts.forEach((chart) => {
        const chartInstance = ChartJS.getChart(chart.id);
        if (!chartInstance) return;

        if (chart.type === 'data') {
          newItemsVisibility.forEach((isVisible, index) => {
            const isDataVisible = chartInstance.getDataVisibility(index);
            if (isDataVisible !== isVisible) {
              chartInstance.toggleDataVisibility(index);
            }
          });
        } else {
          // dataset
          newItemsVisibility.forEach((isVisible, index) => chartInstance.setDatasetVisibility(index, isVisible));
        }
        chartInstance.update();
      });
    },
    [charts, buildInitialVisibility]
  );

  return {
    itemsVisibility,
    toggleVisibility,
    restoreVisibility,
  };
}

export default useChartItemsVisibility;
